/**
 * Google admin — agency-level App credentials only.
 *
 * The previous master-OAuth flow (single agency login that discovered every
 * client's GA4 / Search Console / Google Ads / GMB / YouTube resources and
 * required per-row "Assign to client" + GMB resync + remembered-assignments
 * machinery) has been retired. Every Google connection now lives on the
 * per-client page (`/clients/:clientId/google-feeds`), so all this page
 * keeps is the agency-wide OAuth client_id / secret / Ads developer token
 * that powers those per-client OAuth rounds.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import {
  Chrome, Save, ExternalLink, Eye, EyeOff, Users,
} from "lucide-react";

export default function GoogleIntegrationsPage() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  // If the OAuth callback bounced us back with ?error=... show a toast so
  // the admin knows exactly WHY the connect flow failed (previously the
  // error param sat silently in the URL bar and nothing surfaced it).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (err) {
      const detail = params.get("detail");
      toast.error(
        `Google OAuth: ${err}${detail ? " — " + decodeURIComponent(detail) : ""}`,
        { duration: 12000 },
      );
      // Strip the error params out of the URL so a page refresh doesn't
      // re-fire the same toast.
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
    }
  }, []);

  const [form, setForm] = useState({
    oauth_client_id: "",
    oauth_client_secret: "",
    ads_developer_token: "",
    ads_login_customer_id: "",
    redirect_uri: "",
  });
  const [showSecret, setShowSecret] = useState(false);
  const [showDevToken, setShowDevToken] = useState(false);

  // The backend OAuth callback lives on the API host; build the default so
  // admins can copy/paste it into the Google Cloud Console without thinking.
  const defaultRedirect = `${window.location.origin.replace(/^https?:\/\/[^/]+/, process.env.REACT_APP_BACKEND_URL)}/api/google/oauth/callback`;

  const refresh = async () => {
    try {
      const { data } = await api.get("/integrations/google/settings");
      setSettings(data);
      setForm((f) => ({
        ...f,
        oauth_client_id: data.oauth_client_id || "",
        ads_login_customer_id: data.ads_login_customer_id || "",
        redirect_uri: data.redirect_uri || defaultRedirect,
      }));
    } catch (e) {
      logger.warn("GoogleIntegrations: load settings failed", e);
      setSettings({});
      setForm((f) => ({ ...f, redirect_uri: defaultRedirect }));
    }
  };

  useEffect(() => { refresh(); }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const body = { ...form };
      // Only send secret/token if the admin actually typed something new —
      // keeps stored values intact when the field is left blank.
      if (!body.oauth_client_secret) delete body.oauth_client_secret;
      if (!body.ads_developer_token) delete body.ads_developer_token;
      await api.put("/integrations/google/settings", body);
      toast.success("Settings saved");
      setForm((f) => ({ ...f, oauth_client_secret: "", ads_developer_token: "" }));
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!settings) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  const fieldClass = "rounded-sm font-mono text-xs h-10";

  return (
    <div className="space-y-8 max-w-4xl" data-testid="google-admin-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-gradient-to-br from-[#4285F4] to-[#34A853] text-white">
          <Chrome className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">Integrations</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">Google admin</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Agency-level Google Cloud OAuth credentials. Save the OAuth
            client_id, client_secret, Ads developer token and (optional) Ads
            manager login customer ID once — every per-client Google OAuth
            round (Analytics · Ads · Business Profile · Search Console ·
            YouTube) reuses these.
          </p>
        </div>
      </div>

      {/* Per-client info banner */}
      <div className="rounded-sm border border-blue-200 bg-blue-50 p-4 flex items-start gap-3"
        data-testid="google-per-client-banner">
        <Users className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
        <div className="text-sm text-blue-900">
          <div className="font-semibold">Google connections now live per-client</div>
          <div className="text-[12px] mt-1 leading-relaxed">
            Connect a Google account from each client&apos;s feeds page
            (Clients → pick a client → Google feeds). One OAuth grant per
            client scopes Analytics, Ads, Business Profile, Search Console
            and YouTube resources exclusively to that client — no master
            discovery, no remembered-assignments tab, no manual GMB
            resync. This page only holds the agency-wide app credentials.
          </div>
        </div>
      </div>

      {/* App credentials card */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display font-bold text-xl">App credentials</div>
              <p className="text-xs text-zinc-500">
                Find these in the Google Cloud Console at{" "}
                <a href="https://console.cloud.google.com/apis/credentials"
                  target="_blank" rel="noreferrer" className="underline">
                  console.cloud.google.com/apis/credentials
                  <ExternalLink className="inline h-3 w-3 ml-0.5" />
                </a>.
              </p>
            </div>
            <Badge className={`rounded-sm ${settings.oauth_client_id && settings.has_client_secret
              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : "bg-amber-100 text-amber-800 border-amber-200"}`}
              data-testid="google-master-config-badge">
              {settings.oauth_client_id && settings.has_client_secret
                ? "Configured"
                : "Credentials required"}
            </Badge>
          </div>

          <form onSubmit={save} className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-overline">OAuth client ID</Label>
              <Input value={form.oauth_client_id}
                onChange={(e) => setForm((f) => ({ ...f, oauth_client_id: e.target.value }))}
                className={fieldClass}
                placeholder="123456789-abc.apps.googleusercontent.com"
                data-testid="google-client-id-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">
                OAuth client secret {settings.has_client_secret && (
                  <span className="ml-2 normal-case tracking-normal text-emerald-700 text-[10px]">· stored</span>
                )}
              </Label>
              <div className="flex gap-2">
                <Input type={showSecret ? "text" : "password"}
                  value={form.oauth_client_secret}
                  onChange={(e) => setForm((f) => ({ ...f, oauth_client_secret: e.target.value }))}
                  placeholder={settings.has_client_secret ? "•••••••• (leave blank to keep)" : "Paste secret"}
                  className={`${fieldClass} flex-1`}
                  data-testid="google-client-secret-input" />
                <Button type="button" variant="outline" className="rounded-sm" size="sm"
                  onClick={() => setShowSecret((s) => !s)}>
                  {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="label-overline">
                Google Ads developer token {settings.has_ads_developer_token && (
                  <span className="ml-2 normal-case tracking-normal text-emerald-700 text-[10px]">· stored</span>
                )}
              </Label>
              <div className="flex gap-2">
                <Input type={showDevToken ? "text" : "password"}
                  value={form.ads_developer_token}
                  onChange={(e) => setForm((f) => ({ ...f, ads_developer_token: e.target.value }))}
                  placeholder={settings.has_ads_developer_token ? "•••••••• (leave blank to keep)" : "Paste developer token"}
                  className={`${fieldClass} flex-1`}
                  data-testid="google-ads-dev-token-input" />
                <Button type="button" variant="outline" className="rounded-sm" size="sm"
                  onClick={() => setShowDevToken((s) => !s)}>
                  {showDevToken ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="label-overline">Ads manager login customer ID (optional)</Label>
              <Input value={form.ads_login_customer_id}
                onChange={(e) => setForm((f) => ({ ...f, ads_login_customer_id: e.target.value }))}
                placeholder="123-456-7890"
                className={fieldClass}
                data-testid="google-ads-login-cid-input" />
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <Label className="label-overline">Authorised redirect URI (paste into Google Cloud Console)</Label>
              <Input value={form.redirect_uri} readOnly
                className="rounded-sm font-mono text-[11px] h-9 bg-zinc-50"
                onFocus={(e) => e.target.select()}
                data-testid="google-redirect-uri" />
              <p className="text-[11px] text-zinc-500">
                Add this URI under <span className="font-mono">APIs &amp; Services → Credentials →
                OAuth client → Authorised redirect URIs</span>.
              </p>
            </div>

            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" disabled={busy}
                className="rounded-sm bg-[#4285F4] hover:bg-[#1A73E8] text-white"
                data-testid="google-save-settings">
                <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save credentials"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
